import { Icon } from '@iconify/react';
import VisitorAvatar from './VisitorAvatar';
import VisitorRowActions from './VisitorRowActions';
import { VisitorTypeBadge, VisitorStatusBadge, IdProofBadge } from './VisitorBadges';
import { VisitorLoader, VisitorEmptyState } from './VisitorListStates';
import { fmtDate, fmtPhone, isBlocked } from '../visitorConstants';

const VisitorCard = ({ visitor, actions }) => {
    const blocked = isBlocked(visitor);
    const visits = visitor.visit_count ?? 0;

    return (
        <div
            onClick={() => actions.onView(visitor)}
            className={`tw-border tw-rounded-lg tw-p-3 tw-space-y-2.5 hover:tw-shadow-md tw-transition-shadow tw-cursor-pointer ${blocked ? 'tw-border-rose-100 tw-bg-rose-50/30' : 'tw-border-gray-100'}`}
        >
            <div className="tw-flex tw-items-start tw-gap-2.5">
                <VisitorAvatar visitor={visitor} size={42} />
                <div className="tw-min-w-0 tw-flex-1">
                    <h3 className="tw-mb-0 tw-text-xs tw-font-bold tw-text-gray-800 tw-truncate" title={visitor.name}>{visitor.name}</h3>
                    <p className="tw-mb-0 tw-mt-0.5 tw-text-[11px] tw-text-gray-500 tw-font-mono">{fmtPhone(visitor.phone)}</p>
                </div>
                <VisitorStatusBadge visitor={visitor} />
            </div>

            {(visitor.company_name || visitor.email) && (
                <p className="tw-mb-0 tw-text-[11px] tw-text-gray-500 tw-truncate tw-flex tw-items-center tw-gap-1">
                    <Icon icon={visitor.company_name ? 'mdi:domain' : 'mdi:email-outline'} className="tw-text-gray-400 tw-shrink-0" />
                    <span className="tw-truncate">{visitor.company_name || visitor.email}</span>
                </p>
            )}

            <div className="tw-flex tw-flex-wrap tw-gap-1.5">
                <VisitorTypeBadge value={visitor.visitor_type} />
                {visitor.id_proof_type && <IdProofBadge type={visitor.id_proof_type} pill />}
            </div>

            {blocked && visitor.blocked_reason && (
                <p className="tw-mb-0 tw-text-[10px] tw-text-rose-700 tw-bg-rose-50 tw-border tw-border-rose-100 tw-rounded tw-px-2 tw-py-1 tw-line-clamp-2">
                    {visitor.blocked_reason}
                </p>
            )}

            <div className="tw-flex tw-items-center tw-justify-between tw-text-[10px] tw-text-gray-500 tw-pt-1.5 tw-border-t tw-border-gray-100">
                <span className="tw-flex tw-items-center tw-gap-1">
                    <Icon icon="mdi:counter" className="tw-text-gray-400" />
                    {visits} visit{visits === 1 ? '' : 's'}
                </span>
                <span className="tw-flex tw-items-center tw-gap-1">
                    <Icon icon="mdi:calendar-clock-outline" className="tw-text-gray-400" />
                    {fmtDate(visitor.last_visit_at)}
                </span>
            </div>

            {/* The card itself opens the profile, so the action strip has to stop
                the click from bubbling into that navigation. */}
            <div className="tw-flex tw-items-center tw-justify-end" onClick={e => e.stopPropagation()}>
                <VisitorRowActions visitor={visitor} {...actions} />
            </div>
        </div>
    );
};

const VisitorCardGrid = ({ rows, loading, actions, empty }) => (
    <div className="tw-p-4">
        {loading && <VisitorLoader />}
        {!loading && rows.length === 0 && <VisitorEmptyState {...empty} />}
        {!loading && rows.length > 0 && (
            <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 lg:tw-grid-cols-3 tw-gap-3">
                {rows.map(v => <VisitorCard key={v.visitor_id} visitor={v} actions={actions} />)}
            </div>
        )}
    </div>
);

export default VisitorCardGrid;
